/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: Language List Section for Video Translator landing page
 */
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { RightContext } from '@/components/RightProvider/context';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';

import styles from './index.module.scss';

// 按设计稿横向顺序排列的语言列表（3列布局，从左到右阅读）
const LANGUAGES = [
  // Row 1
  { name: 'Chinese', icon: '/images/country/flag-cn.png' },
  { name: 'Cantonese', icon: '/images/country/flag-yue.png' },
  { name: 'English', icon: '/images/country/flag-us.png' },
  // Row 2
  { name: 'Japanese', icon: '/images/country/flag-jp.png' },
  { name: 'Korean', icon: '/images/country/flag-kr.png' },
  { name: 'Indonesian', icon: '/images/country/flag-id.png' },
  // Row 3
  { name: 'Filipino', icon: '/images/country/flag-ph.png' },
  { name: 'Russian', icon: '/images/country/flag-ru.png' },
  { name: 'Spanish', icon: '/images/country/flag-es.png' },
  // Row 4
  { name: 'German', icon: '/images/country/flag-de.png' },
  { name: 'Hindi', icon: '/images/country/flag-in.png' },
  { name: 'Arabic', icon: '/images/country/flag-ar.png' },
  // Row 5
  { name: 'Vietnamese', icon: '/images/country/flag-vn.png' },
  { name: 'French', icon: '/images/country/flag-fr.png' },
  { name: 'Portuguese', icon: '/images/country/flag-pt.png' },
  // Row 6
  { name: 'Tamil', icon: '/images/country/flag-tam.png' },
  { name: 'Malay', icon: '/images/country/flag-my.png' },
  { name: 'Bulgarian', icon: '/images/country/flag-bg.png' },
  // Row 7
  { name: 'Czech', icon: '/images/country/flag-cz.png' },
  { name: 'Danish', icon: '/images/country/flag-dk.png' },
  { name: 'Greek', icon: '/images/country/flag-gr.png' },
  // Row 8
  { name: 'Finnish', icon: '/images/country/flag-fi.png' },
  { name: 'Croatian', icon: '/images/country/flag-hr.png' },
  { name: 'Italian', icon: '/images/country/flag-it.png' },
  // Row 9
  { name: 'Norwegian', icon: '/images/country/flag-no.png' },
  { name: 'Dutch', icon: '/images/country/flag-nl.png' },
  { name: 'Polish', icon: '/images/country/flag-pl.png' },
  // Row 10
  { name: 'Romanian', icon: '/images/country/flag-ro.png' },
  { name: 'Slovakian', icon: '/images/country/flag-sk.png' },
  { name: 'Swedish', icon: '/images/country/flag-se.png' },
  // Row 11
  { name: 'Turkish', icon: '/images/country/flag-tr.png' },
  { name: 'Ukrainian', icon: '/images/country/flag-ua.png' },
  { name: 'Urdu', icon: '/images/country/flag-ur.png' },
  // Row 12
  { name: 'Mongolian', icon: '/images/country/flag-mn.png' },
  { name: 'Thai', icon: '/images/country/flag-th.png' },
  { name: '', icon: '' }, // 空白占位
];

export interface LanguageListSectionProps {
  /** 区块标题 */
  title: string;
  /** 区块副标题 */
  subtitle: string;
  /** CTA 按钮文本 */
  ctaText: string;
}

interface LanguageCardProps {
  icon?: string;
  name: string;
}

const LanguageCard: React.FC<LanguageCardProps> = ({ icon, name }) => {
  return (
    <div className={styles.languageCard}>
      <div className={styles.flagWrapper}>
        {icon ? (
          <Image
            src={icon}
            alt={name}
            width={40}
            height={40}
            className={styles.flagImage}
            loading="lazy"
          />
        ) : (
          <div className={styles.flagPlaceholder} />
        )}
      </div>
      <span className={styles.languageName}>{name}</span>
    </div>
  );
};

const LanguageListSection: React.FC<LanguageListSectionProps> = ({
  title,
  subtitle,
  ctaText,
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);

  // CTA 点击
  const handleCtaClick = async () => {
    if (!rightContext.getIsLoggedIn()) {
      await rightContext.getUserLoginState({
        onReady: () => {
          router.push(VIDEO_TRANSLATOR_URL);
        },
      });
      return;
    }
    router.push(VIDEO_TRANSLATOR_URL);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* 副标题 */}
        <p className={styles.subtitle}>{subtitle}</p>

        {/* CTA 按钮 */}
        <div className={styles.ctaWrapper}>
          <StartButton
            text={ctaText}
            onClick={handleCtaClick}
            className={styles.ctaButton}
          />
        </div>

        {/* 语言网格 */}
        <div className={styles.gridWrapper}>
          <div className={styles.grid}>
            {LANGUAGES.map((lang, index) =>
              lang.name ? (
                <LanguageCard key={index} icon={lang.icon} name={lang.name} />
              ) : (
                <div key={index} className={styles.emptyCard} />
              ),
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LanguageListSection;
